import classNames from "classnames";

import { FC, ReactNode, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import useI18n from "../../hooks/useTranslations";
import { useSystemsApi } from "../../http/useSystems";
import { getMinutesRoundedTimestamp } from "../../utils";
import CreateConfigurationButton from "../configurations/CreateConfigurationButton";
import SvgEdit from "../icons/Edit";
import SvgSettings from "../icons/Settings";
import SvgNoImage from "../icons/SvgNoImage";
import Button from "../ui/Button";
import FlexContent from "../ui/FlexContent";
import Header, { HeaderPath } from "../ui/Header";
import Icon from "../ui/Icon";
import WhiteSpacePreWrapText from "../ui/WhiteSpacePreWrapText";
import styles from "./System.module.css";

const System: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { permissions } = useAuthContext();
  const systemsApi = useSystemsApi();
  const navigate = useNavigate();

  const aborter = useRef(new AbortController());
  const { data, error, trigger } = systemsApi.getSystem(aborter.current.signal);

  const path: HeaderPath[] = [{ display: i18n.translation.common.home, url: "/" }];

  if (data?.name) {
    path.push({ display: data.name });
  }

  useEffect(() => {
    systemId && trigger(systemId);
  }, [systemId]);

  const isLoading = !data && !error;

  const buttons: ReactNode[] = [
    <CreateConfigurationButton
      systemId={systemId!}
      type="system"
      title={i18n.translation.configurations.create}
      disabled={!!error || isLoading}
    />,
  ];

  if (permissions.admin.canManageSystems) {
    buttons.push(<Button glyph={SvgEdit} buttonProps={{ onClick: () => navigate(`/admin/systems/${systemId}`) }} />);
    buttons.push(
      <Button glyph={SvgSettings} buttonProps={{ onClick: () => navigate(`/admin/systems/${systemId}/settings`) }} />,
    );
  }

  return (
    <>
      <Header
        title={data?.name ?? i18n.translation.common.loading}
        error={error}
        // TODO add "Edit item" button if the user has admin roles (or edit-item-permission)
        buttons={buttons}
        path={path}
      />
      <FlexContent padding>
        <div className={styles.container}>
          <div className={styles.imgContainer}>
            <div className={classNames(styles.img, { [styles.background]: !data?.image })}>
              {data?.image ? (
                <img src={`${import.meta.env.VITE_BUCKET_URL}${data.image}?${getMinutesRoundedTimestamp()}`} />
              ) : (
                <Icon glyph={SvgNoImage} />
              )}
            </div>
          </div>
          <WhiteSpacePreWrapText>{data?.descriptionLong}</WhiteSpacePreWrapText>
        </div>
      </FlexContent>
    </>
  );
};

export default System;
